import React, { useState, useEffect } from 'react';
import { useAuth, apiClient } from '../auth/AuthContext';
import TimelineView from './TimelineView';

export interface DailySummary {
  date: string;
  totalHours: number;
  totalCost: number;
  requests: number;
}

export interface DayDetailRequest {
  id: string;
  startDateTime: string;
  endDateTime: string;
  areas?: string[];
  requesterUserId: string;
  reason?: string;
}

export interface ReportData {
  summary: DailySummary[];
  totals: {
    totalHours: number;
    totalCost: number;
    totalRequests: number;
  };
  period: {
    from: string;
    to: string;
  };
  buildingId: string;
}

export interface ReportingPageProps {
  onNavigateToRequests?: (filters?: {
    area?: string;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
  }) => void;
}

const ReportingPage: React.FC<ReportingPageProps> = ({ onNavigateToRequests }) => {
  const { user, token } = useAuth();
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [dateRange, setDateRange] = useState(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    return {
      from: firstDay.toISOString().split('T')[0],
      to: lastDay.toISOString().split('T')[0],
    };
  });
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [calendarData, setCalendarData] = useState<{[date: string]: {hours: number, cost: number, requests: number}}>({});
  const [dayDetailRequests, setDayDetailRequests] = useState<DayDetailRequest[]>([]);
  const [dayDetailLoading, setDayDetailLoading] = useState(false);


  const fetchReportData = async () => {
    // Require auth and a valid building context before fetching
    if (!token || !user?.buildingId || typeof user.buildingId !== 'string' || user.buildingId.trim() === '') {
      setError('Building context is missing. Please ensure you are logged in and have a building assigned.');
      console.error('Building context is missing. Please ensure you are logged in and have a building assigned.');
      console.error('token:', token);
      console.error('user:', user);
      console.error('user?.buildingId:', user?.buildingId);
      console.error('typeof user.buildingId:', typeof user?.buildingId);
      console.error('user.buildingId.trim():', user?.buildingId.trim());
      return;
    }

    setLoading(true);
    setError('');

    try {
      let fromEpoch: number, toEpoch: number;
      
      if (showAdvanced) {
        const fromD = new Date(dateRange.from + 'T00:00:00Z');
        const toD = new Date(dateRange.to + 'T23:59:59Z');
        fromEpoch = fromD.getTime();
        toEpoch = toD.getTime();
      } else {
        // Use selected month
        const [year, month] = selectedMonth.split('-').map(Number);
        const firstDay = new Date(year, month - 1, 1);
        const lastDay = new Date(year, month, 0);
        fromEpoch = firstDay.getTime();
        toEpoch = lastDay.getTime() + (24 * 60 * 60 * 1000 - 1);
      }
      
      const data = await apiClient.get<ReportData>(
        `/reports/summary?from=${encodeURIComponent(String(fromEpoch))}&to=${encodeURIComponent(String(toEpoch))}&buildingId=${encodeURIComponent(user.buildingId)}`
      );
      setReportData(data);
      
      // Process calendar data (normalize backend epoch date to ISO YYYY-MM-DD key)
      const calData: {[date: string]: {hours: number, cost: number, requests: number}} = {};
      data.summary.forEach(day => {
        const isoKey = day.date;
        calData[isoKey] = {
          hours: day.totalHours,
          cost: day.totalCost,
          requests: day.requests
        };
      });
      setCalendarData(calData);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch report data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, [dateRange, selectedMonth, showAdvanced, token, user?.buildingId]);

  const fetchDayDetailRequests = async (dayDate: string) => {
    if (!token || !user?.buildingId) return;

    setDayDetailLoading(true);
    try {
      const dayStart = new Date(dayDate);
      const dayEnd = new Date(dayDate);
      dayEnd.setDate(dayEnd.getDate() + 1);

      const result: any = await apiClient.get(
        `/requests?buildingId=${user.buildingId}&status=APPROVED&from=${dayStart.getTime()}&to=${dayEnd.getTime()}&pageSize=100`
      );
      setDayDetailRequests(result.data || []);
    } catch (err: any) {
      console.error('Error fetching day details:', err);
      setDayDetailRequests([]);
    } finally {
      setDayDetailLoading(false);
    }
  };

  useEffect(() => {
    if (selectedDay) {
      fetchDayDetailRequests(selectedDay);
    }
  }, [selectedDay, token, user?.buildingId]);

  const exportToCSV = () => {
    if (!reportData) return;

    const csvData = [
      ['Date', 'Hours', 'Cost ($)', 'Requests'],
      ...reportData.summary.map(day => [
        day.date,
        day.totalHours.toFixed(2),
        day.totalCost.toFixed(2),
        day.requests.toString()
      ]),
      ['', '', '', ''], // Empty row
      ['TOTALS', reportData.totals.totalHours.toFixed(2), reportData.totals.totalCost.toFixed(2), reportData.totals.totalRequests.toString()]
    ];

    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `after-hours-report-${dateRange.from}-to-${dateRange.to}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleDateChange = (field: 'from' | 'to', value: string) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const setScrollMonth = (direction: 'prev' | 'next') => {
    // const now = new Date();
    const [year, month] = selectedMonth.split('-').map(Number);
    let targetDate: Date;
    
    switch (direction) {
      // Remember Date takes in month as 0-11
      case 'prev':
        targetDate = new Date(year, month - 2, 1);
        break;
      case 'next':
        targetDate = new Date(year, month, 1);
        break;
    }
    
    setSelectedMonth(`${targetDate.getFullYear()}-${String(targetDate.getMonth() + 1).padStart(2, '0')}`);
  };

  // const setPresetMonth = (preset: 'thisMonth' | 'lastMonth') => {
  //   const now = new Date();
  //   let targetDate: Date;
    
  //   switch (preset) {
  //     case 'thisMonth':
  //       targetDate = now;
  //       break;
  //     case 'lastMonth':
  //       targetDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  //       break;
  //   }
    
  //   setSelectedMonth(`${targetDate.getFullYear()}-${String(targetDate.getMonth() + 1).padStart(2, '0')}`);
  // };

  const setPresetRange = (preset: 'thisMonth' | 'lastMonth' | 'last30Days' | 'last90Days') => {
    const now = new Date();
    let from: Date, to: Date;

    switch (preset) {
      case 'thisMonth':
        from = new Date(now.getFullYear(), now.getMonth(), 1);
        to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'lastMonth':
        from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        to = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'last30Days':
        from = new Date(now);
        from.setDate(now.getDate() - 30);
        to = new Date(now);
        break;
      case 'last90Days':
        from = new Date(now);
        from.setDate(now.getDate() - 90);
        to = new Date(now);
        break;
    }

    setDateRange({
      from: from.toISOString().split('T')[0],
      to: to.toISOString().split('T')[0],
    });
  };

  // Calendar helper functions
  const generateCalendarDays = () => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const firstDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    
    const days: Array<{date: string | null, data?: {hours: number, cost: number, requests: number}}> = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push({ date: null });
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      // Normalize to ISO date (YYYY-MM-DD) to match backend "summary.date" keys
      // Use UTC to avoid timezone shifts when converting to ISO
      const isoKey = new Date(Date.UTC(year, month - 1, day)).toISOString().split('T')[0];
      days.push({ 
        date: `${day}`,
        data: calendarData[isoKey]
      });
    }
    
    return days;
  };
  
  const getReportMinMax = (reportData: ReportData) => {
    const min = reportData.summary.reduce((min, day) => Math.min(min, day.totalCost), reportData.summary[0].totalCost);
    const max = reportData.summary.reduce((max, day) => Math.max(max, day.totalCost), reportData.summary[0].totalCost);
    return [min, max];
  };

  const getCalendarDayColor = (dayData?: {hours: number, cost: number, requests: number}) => {
    if (!dayData || !reportData) return '#f7fafc';
    
    // const avgCost = reportData.totals.totalCost / reportData.summary.length;
    // const ratio = dayData.cost / avgCost;
    const [min, max] = getReportMinMax(reportData);
    const ratio = (dayData.cost - min) / (max - min);
    const green = [0x68, 0xd3, 0x91];
    const red = [0xf5, 0x65, 0x65];
    const yellow = [0xf6, 0xe0, 0x5e];
    if(ratio < 0.5) {
      const color = [
        Math.round(green[0] + (yellow[0] - green[0]) * ratio),
        Math.round(green[1] + (yellow[1] - green[1]) * ratio),
        Math.round(green[2] + (yellow[2] - green[2]) * ratio)
      ];
      return `rgb(${color[0]}, ${color[1]}, ${color[2]})`;
    } else {
      const color = [
        Math.round(yellow[0] + (red[0] - yellow[0]) * ratio),
        Math.round(yellow[1] + (red[1] - yellow[1]) * ratio),
        Math.round(yellow[2] + (red[2] - yellow[2]) * ratio)
      ];
      return `rgb(${color[0]}, ${color[1]}, ${color[2]})`;
    }

    if (ratio <= 0.8) return '#68d391'; // Green for below average
    if (ratio <= 1.2) return '#f6e05e'; // Yellow for average
    return '#f56565'; // Red for above average
  };

  return (
    <div className="ah-shell">
      <div className="ah-panel">
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div className="ah-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <div>
                <h1 className="ah-title">After Hours Reports</h1>
                <p className="muted">Summary of approved after hours requests and associated costs</p>
              </div>
              
              <div style={{ display: 'flex', gap: 8 }}>
                {reportData && (
                  <>
                    <button onClick={exportToCSV} className="ah-btn">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7,10 12,15 17,10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      Export CSV
                    </button>
                    {onNavigateToRequests && (
                      <button 
                        onClick={() => onNavigateToRequests({
                          dateFrom: showAdvanced ? dateRange.from : `${selectedMonth}-01`,
                          dateTo: showAdvanced ? dateRange.to : 
                            new Date(parseInt(selectedMonth.split('-')[0]), parseInt(selectedMonth.split('-')[1]), 0)
                              .toISOString().split('T')[0]
                        })} 
                        className="ah-btn ah-btn-outline"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M9 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2h-4"/>
                          <polyline points="16,6 12,2 8,6"/>
                          <line x1="12" y1="2" x2="12" y2="15"/>
                        </svg>
                        View All Requests
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Date Range Controls */}
            <div style={{ marginBottom: 24 }}>
              {!showAdvanced ? (
                <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center' }}>
                    <button onClick={() => setScrollMonth('prev')} className="ah-btn ah-btn-outline ah-month-button">&lt;</button>
                    <div>
                      {/* <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>Month</label> */}
                      <input
                        type="month"
                        className="ah-input ah-month-input"
                        style={{ width: 180 }}
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                      />
                      {/* <button onClick={() => setPresetMonth('thisMonth')} className="ah-btn ah-btn-outline ah-month-button"></button> */}
                    </div>
                    <button onClick={() => setScrollMonth('next')} className="ah-btn ah-btn-outline ah-month-button">&gt;</button>
                  </div>
                  
                </div>
              ) : (
                <div>
                  <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>From</label>
                      <input
                        type="date"
                        className="ah-input"
                        style={{ width: 150 }}
                        value={dateRange.from}
                        onChange={(e) => handleDateChange('from', e.target.value)}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>To</label>
                      <input
                        type="date"
                        className="ah-input"
                        style={{ width: 150 }}
                        value={dateRange.to}
                        onChange={(e) => handleDateChange('to', e.target.value)}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button onClick={() => setPresetRange('thisMonth')} className="ah-btn ah-btn-outline">This Month</button>
                      <button onClick={() => setPresetRange('lastMonth')} className="ah-btn ah-btn-outline">Last Month</button>
                      <button onClick={() => setPresetRange('last30Days')} className="ah-btn ah-btn-outline">Last 30 Days</button>
                      <button onClick={() => setPresetRange('last90Days')} className="ah-btn ah-btn-outline">Last 90 Days</button>
                    </div>
                    <button 
                      onClick={() => setShowAdvanced(false)} 
                      className="ah-btn ah-btn-outline"
                      style={{ marginLeft: 'auto' }}
                    >
                      Simple View
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Summary Cards */}
            {reportData && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 32 }}>
                <div className="ah-card" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 600, color: 'var(--primary)' }}>
                    {reportData.totals.totalRequests}
                  </div>
                  <div className="muted">Total Requests</div>
                </div>
                <div className="ah-card" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 600, color: 'var(--primary)' }}>
                    {reportData.totals.totalHours.toFixed(1)}
                  </div>
                  <div className="muted">Total Hours</div>
                </div>
                <div className="ah-card" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 600, color: 'var(--primary)' }}>
                    ${reportData.totals.totalCost.toFixed(2)}
                  </div>
                  <div className="muted">Total Cost</div>
                </div>
                <div className="ah-card" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 600, color: 'var(--primary)' }}>
                    ${(reportData.totals.totalCost / Math.max(reportData.totals.totalHours, 1)).toFixed(2)}
                  </div>
                  <div className="muted">Avg Rate/Hour</div>
                </div>
              </div>
            )}

            {/* Calendar View */}
            {reportData && !showAdvanced && (
              <div style={{ marginBottom: 32 }}>
                <h3 style={{ marginBottom: 16 }}>Calendar View</h3>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(7, 1fr)', 
                  gap: 1, 
                  backgroundColor: '#e2e8f0',
                  padding: 1,
                  borderRadius: 6
                }}>
                  {/* Calendar headers */}
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} style={{ 
                      padding: 8, 
                      backgroundColor: '#f7fafc', 
                      fontWeight: 600, 
                      textAlign: 'center',
                      fontSize: 12
                    }}>
                      {day}
                    </div>
                  ))}
                  
                  {/* Calendar days */}
                  {generateCalendarDays().map((day, index) => (
                    <div 
                      key={index}
                      style={{ 
                        minHeight: 60,
                        backgroundColor: day.date ? getCalendarDayColor(day.data) : '#f7fafc',
                        padding: 4,
                        position: 'relative',
                        cursor: day.date && day.data ? 'pointer' : 'default',
                        opacity: day.date ? 1 : 0.3
                      }}
                      onClick={() => {
                        if (day.date && day.data) {
                          const [year, month] = selectedMonth.split('-').map(Number);
                          const clickedDate = new Date(year, month - 1, parseInt(day.date)).toDateString();
                          setSelectedDay(clickedDate);
                        }
                      }}
                    >
                      {day.date && (
                        <>
                          <div style={{ fontSize: 12, fontWeight: 500 }}>{day.date}</div>
                          {day.data && (
                            <div style={{ fontSize: 10, marginTop: 2 }}>
                              <div>${day.data.cost.toFixed(0)}</div>
                              <div>{day.data.hours.toFixed(1)}h</div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Calendar legend */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 8, fontSize: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 12, height: 12, backgroundColor: '#68d391', borderRadius: 2 }}></div>
                    <span>Below Average</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 12, height: 12, backgroundColor: '#f6e05e', borderRadius: 2 }}></div>
                    <span>Average</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 12, height: 12, backgroundColor: '#f56565', borderRadius: 2 }}></div>
                    <span>Above Average</span>
                  </div>
                </div>
              </div>
            )}

            {/* Day Detail View */}
            {selectedDay && (
              <div style={{ marginBottom: 32 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h3>Day Detail View - {new Date(selectedDay).toLocaleDateString()}</h3>
                  <button 
                    onClick={() => setSelectedDay(null)} 
                    className="ah-btn ah-btn-outline"
                  >
                    Close Detail
                  </button>
                </div>

                {dayDetailLoading ? (
                  <div style={{ textAlign: 'center', padding: 20 }}>Loading day details...</div>
                ) : (
                  <TimelineView dayDetailRequests={dayDetailRequests} day={selectedDay ? new Date(selectedDay) : undefined} />
                )}
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <div>Loading report data...</div>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div style={{ 
                color: '#e53e3e', 
                backgroundColor: '#fed7d7', 
                border: '1px solid #feb2b2', 
                borderRadius: 6, 
                padding: 16, 
                marginBottom: 24 
              }}>
                {error}
              </div>
            )}

            {/* Report Table */}
            {reportData && reportData.summary.length > 0 && (
              <div>
                <h3 style={{ marginBottom: 16 }}>Daily Breakdown</h3>
                <div className="ah-table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th style={{ textAlign: 'right' }}>Hours</th>
                        <th style={{ textAlign: 'right' }}>Cost</th>
                        <th style={{ textAlign: 'right' }}>Requests</th>
                        <th style={{ textAlign: 'right' }}>Avg Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.summary.map((day, index) => (
                        <tr key={index}>
                          <td>{new Date(day.date).getDate()}/{new Date(day.date).getMonth()+1}/{new Date(day.date).getFullYear()}</td>
                          <td style={{ textAlign: 'right' }}>{day.totalHours.toFixed(1)}</td>
                          <td style={{ textAlign: 'right' }}>${day.totalCost.toFixed(2)}</td>
                          <td style={{ textAlign: 'right' }}>
                            {onNavigateToRequests ? (
                              <button
                                onClick={() => {
                                  const dayStart = new Date(day.date);
                                  const dayEnd = new Date(day.date);
                                  dayEnd.setDate(dayEnd.getDate() + 1);
                                  onNavigateToRequests({
                                    dateFrom: dayStart.toISOString().split('T')[0],
                                    dateTo: dayEnd.toISOString().split('T')[0]
                                  });
                                }}
                                style={{ 
                                  background: 'none', 
                                  border: 'none', 
                                  color: 'var(--primary)', 
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                  font: 'inherit'
                                }}
                              >
                                {day.requests}
                              </button>
                            ) : (
                              day.requests
                            )}
                          </td>
                          <td style={{ textAlign: 'right' }}>${(day.totalCost / Math.max(day.totalHours, 1)).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ borderTop: '2px solid var(--border)', fontWeight: 600 }}>
                        <td>TOTAL</td>
                        <td style={{ textAlign: 'right' }}>{reportData.totals.totalHours.toFixed(1)}</td>
                        <td style={{ textAlign: 'right' }}>${reportData.totals.totalCost.toFixed(2)}</td>
                        <td style={{ textAlign: 'right' }}>{reportData.totals.totalRequests}</td>
                        <td style={{ textAlign: 'right' }}>${(reportData.totals.totalCost / Math.max(reportData.totals.totalHours, 1)).toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {/* No Data State */}
            {reportData && reportData.summary.length === 0 && (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <div className="muted">No approved requests found for the selected period.</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportingPage;